'use client';

import { useState, useTransition } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordSettingsSchema, SetPasswordSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslations } from '@/hooks/useTranslations';
import { passwordSettings, setPassword } from '@/actions';
import { SettingsSectionWrapper } from './settings-section-wrapper';

interface PasswordSettingsFormProps {
  hasPassword: boolean;
}

export function PasswordSettingsForm({
  hasPassword,
}: PasswordSettingsFormProps) {
  const t = useTranslations('UserSettings');
  const common = useTranslations('Common');

  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');

  const [isPending, startTransition] = useTransition();

  const changeForm = useForm<z.infer<typeof PasswordSettingsSchema>>({
    resolver: zodResolver(PasswordSettingsSchema),
    defaultValues: {
      password: undefined,
      newPassword: undefined,
    },
  });

  const setForm = useForm<z.infer<typeof SetPasswordSchema>>({
    resolver: zodResolver(SetPasswordSchema),
    defaultValues: {
      newPassword: '',
    },
  });

  const onChange = (values: z.infer<typeof PasswordSettingsSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      passwordSettings(values)
        .then((data) => {
          if (data.success) {
            setSuccess(data.success);
            changeForm.reset();
          } else {
            setError(data.error);
          }
        })
        .catch((err) => {
          console.error(err);
          setError('Something went wrong!');
        });
    });
  };

  const onSet = (values: z.infer<typeof SetPasswordSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      setPassword(values)
        .then((data) => {
          if (data.success) {
            setSuccess(data.success);
            setForm.reset();
          } else {
            setError(data.error);
          }
        })
        .catch((err) => {
          console.error(err);
          setError('Something went wrong!');
        });
    });
  };

  return (
    <SettingsSectionWrapper
      title={t('passwordInfo')}
      description={hasPassword ? t('passwordDesc') : t('setPasswordDesc')}
      error={error}
      success={success}
      footer={
        <div className="flex gap-3">
          <Button
            type="submit"
            form="password-form"
            className="flex-1"
            disabled={isPending}
          >
            {isPending ? common('saving') || 'Saving...' : common('save')}
          </Button>
        </div>
      }
    >
      {hasPassword ? (
        <Form {...changeForm}>
          <form
            id="password-form"
            onSubmit={changeForm.handleSubmit(onChange)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={changeForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('currentPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isPending}
                        placeholder="******"
                        type="password"
                        autoComplete="current-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={changeForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('newPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isPending}
                        placeholder="******"
                        type="password"
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      ) : (
        <Form {...setForm}>
          <form
            id="password-form"
            onSubmit={setForm.handleSubmit(onSet)}
            className="space-y-4"
          >
            <FormField
              control={setForm.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('newPassword')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending}
                      placeholder="******"
                      type="password"
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      )}
    </SettingsSectionWrapper>
  );
}
